import type { FollowUpSheetResponse } from '@lms/shared'
import { ArrowLeft } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { FollowUpPhaseCard } from '@/components/training/FollowUpPhaseCard'
import { SkillCheckGuideline } from '@/components/training/SkillCheckGuideline'
import { StatusBadge } from '@/components/training/StatusBadge'
import { SupervisorAssignmentCard } from '@/components/training/SupervisorAssignmentCard'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useAddFollowUpComment,
  useFollowUpSheet,
  useReturnEvaluation,
  useSubmitEvaluation,
} from '@/hooks/use-follow-ups'
import { useTenantPath } from '@/lib/tenant'

function backPathFor(viewer: FollowUpSheetResponse['viewer'] | undefined): string {
  if (viewer?.isTrainee) return '/my-enrollments'
  if (viewer?.isSupervisor) return '/subordinates'
  return '/teacher/trainings'
}

/** 操作を実行し、結果をトーストで通知する。成功した場合のみ true */
async function runWithToast(
  action: () => Promise<unknown>,
  successMessage: string,
  failureMessage: string,
): Promise<boolean> {
  try {
    await action()
    toast.success(successMessage)
    return true
  } catch (err) {
    toast.error(err instanceof Error ? err.message : failureMessage)
    return false
  }
}

export default function FollowUpPage() {
  const { enrollmentId } = useParams<{ enrollmentId: string }>()
  const tp = useTenantPath()
  const { data: sheet, isLoading } = useFollowUpSheet(enrollmentId ?? '')
  const submitEvaluation = useSubmitEvaluation()
  const returnEvaluation = useReturnEvaluation()
  const addComment = useAddFollowUpComment()

  if (isLoading) {
    return (
      <div className="max-w-3xl space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64" />
        <Skeleton className="h-64" />
      </div>
    )
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center gap-3">
        <Link to={tp(backPathFor(sheet?.viewer))}>
          <Button variant="ghost" size="sm">
            <ArrowLeft className="mr-1 h-4 w-4" />
            戻る
          </Button>
        </Link>
        <h1 className="text-2xl font-bold">スキルチェック</h1>
      </div>

      {sheet && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">受講情報</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-3 text-sm">
            <span>{sheet.enrollment.trainingTitle ?? '研修'}</span>
            {!sheet.viewer.isTrainee && (
              <span className="text-muted-foreground">受講者: {sheet.enrollment.userName}</span>
            )}
            <span className="text-muted-foreground">
              上司評価者: {sheet.supervisor?.name ?? '未設定'}
              {sheet.supervisor?.isAssigned && '（個別指定）'}
            </span>
            <StatusBadge type="enrollment" status={sheet.enrollment.status} />
          </CardContent>
        </Card>
      )}

      {sheet?.viewer.isAdmin && (
        <SupervisorAssignmentCard
          enrollmentId={sheet.enrollment.id}
          supervisor={sheet.supervisor}
        />
      )}

      <SkillCheckGuideline note={sheet?.skillCheckNote} />

      {!sheet || sheet.phases.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">フォローアップはありません</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {sheet.phases.map((phase) => (
            <FollowUpPhaseCard
              key={phase.id}
              phase={phase}
              skillItems={sheet.skillItems}
              viewer={sheet.viewer}
              onSubmitEvaluation={(followUpId, evaluatorType, scores) =>
                runWithToast(
                  () =>
                    submitEvaluation.mutateAsync({ followUpId, evaluatorType, data: { scores } }),
                  '評価を提出しました',
                  '評価の提出に失敗しました',
                )
              }
              onReturnEvaluation={(followUpId, evaluatorType, reason) =>
                runWithToast(
                  () =>
                    returnEvaluation.mutateAsync({ followUpId, evaluatorType, data: { reason } }),
                  '評価を差し戻しました',
                  '差し戻しに失敗しました',
                )
              }
              onAddComment={(followUpId, content) =>
                runWithToast(
                  () => addComment.mutateAsync({ id: followUpId, data: { content } }),
                  'コメントを追加しました',
                  'コメントの追加に失敗しました',
                )
              }
              isSubmitting={submitEvaluation.isPending}
              isReturning={returnEvaluation.isPending}
              isCommenting={addComment.isPending}
            />
          ))}
        </div>
      )}
    </div>
  )
}
